/**
 * TNCY-6 §d — customer read projection (XT-D21/D22, AC-4).
 *
 * A customer-role caller (`project_customer` / `contract_customer`) sees the
 * project OUTCOME only — name, site/turbines, state, progress, and (when a
 * surface exists) published reports. They must NOT see the commercial /
 * workshop chain: contracts, inspection instructions, raw findings, the
 * subcontractor / reporting chain.
 *
 * AC-4 is "ABSENT from the rendered data, not just hidden by CSS". The BE
 * already strips chain/commercial fields from a customer projection, but the
 * FE defends in depth: this projection deletes any chain field that ever
 * leaks onto a customer row BEFORE it reaches a column renderer, so a CSS-
 * hidden cell can never carry the data. The red-team test asserts the
 * projected object contains none of the forbidden keys.
 */
import type { ProjectListItem } from "../types.js";

/** Fields a customer must never see on a project row (the chain + commercial
 *  surface — XT-D21/D22 gap D). The contractor / reporting chain identifies
 *  WHO performs and reports; customers see outcome, never the chain. */
export const CUSTOMER_FORBIDDEN_KEYS = [
  "contractorTenantId",
  "contractorTenantName",
  "reportingSubcontractorTenantId",
] as const;

/**
 * The shape a customer row is allowed to carry. Note the absence of every
 * `contractor*` / `reporting*` field — they aren't `null`-ed, they're not in
 * the type at all, so a renderer literally cannot read them.
 */
export interface CustomerProjectRow {
  projectId: string;
  projectCode: number;
  projectName: string;
  startDate: string | null;
  customerTenantId: string | null;
  customerTenantName: string | null;
  totalAssets: number;
  progressPct: number;
  state: ProjectListItem["state"];
  ownership?: ProjectListItem["ownership"];
  ownerTenantName?: string | null;
  role?: ProjectListItem["role"];
}

/**
 * Strip a project row to the customer outcome surface. Returns a NEW object
 * that physically omits the chain/commercial keys (defence in depth over the
 * BE projection — AC-4).
 */
export function toCustomerProjectRow(row: ProjectListItem): CustomerProjectRow {
  return {
    projectId: row.projectId,
    projectCode: row.projectCode,
    projectName: row.projectName,
    startDate: row.startDate,
    customerTenantId: row.customerTenantId,
    customerTenantName: row.customerTenantName,
    totalAssets: row.totalAssets,
    progressPct: row.progressPct,
    state: row.state,
    ...(row.ownership !== undefined ? { ownership: row.ownership } : {}),
    ...(row.ownerTenantName !== undefined
      ? { ownerTenantName: row.ownerTenantName }
      : {}),
    ...(row.role !== undefined ? { role: row.role } : {}),
  };
}

/**
 * Assert (for tests + a runtime guard) that a projected customer row carries
 * none of the forbidden chain keys. Returns the offending keys (empty when
 * clean). Used by the red-team test and as a cheap dev-time invariant.
 */
export function findChainLeak(row: object): string[] {
  return CUSTOMER_FORBIDDEN_KEYS.filter((k) =>
    Object.prototype.hasOwnProperty.call(row, k),
  );
}
